class ProductTabs extends HTMLElement {
  #controller = new AbortController();

  /** @type {NodeListOf<HTMLButtonElement>} */
  get #tabs() {
    return this.querySelectorAll('[role="tab"]');
  }

  /** @type {NodeListOf<HTMLElement>} */
  get #panels() {
    return this.querySelectorAll('[role="tabpanel"]');
  }

  connectedCallback() {
    const { signal } = this.#controller;

    this.#tabs.forEach((tab) => {
      tab.addEventListener('click', this.#handleTabClick, { signal });
      tab.addEventListener('keydown', this.#handleTabKeydown, { signal });
    });

    // Size chart unit toggle
    this.querySelectorAll('.size-chart__toggle-btn').forEach((btn) => {
      btn.addEventListener('click', this.#handleUnitToggle, { signal });
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * @param {Event} event
   */
  #handleTabClick = (event) => {
    const tab = /** @type {HTMLButtonElement} */ (event.currentTarget);
    this.#activateTab(tab);
  };

  /**
   * Arrow key navigation between tabs
   * @param {KeyboardEvent} event
   */
  #handleTabKeydown = (event) => {
    const tabs = [...this.#tabs];
    const currentIndex = tabs.indexOf(/** @type {HTMLButtonElement} */ (event.currentTarget));

    let newIndex;
    if (event.key === 'ArrowRight') {
      newIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft') {
      newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      newIndex = 0;
    } else if (event.key === 'End') {
      newIndex = tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    const nextTab = tabs[newIndex];
    if (!nextTab) return;
    this.#activateTab(nextTab);
    nextTab.focus();
  };

  /**
   * @param {HTMLButtonElement} tab
   */
  #activateTab(tab) {
    const panelId = tab.getAttribute('aria-controls');
    if (!panelId) return;

    // Deactivate all tabs
    this.#tabs.forEach((t) => {
      t.setAttribute('aria-selected', 'false');
      t.setAttribute('tabindex', '-1');
    });

    // Hide all panels
    this.#panels.forEach((p) => {
      p.hidden = true;
    });

    // Activate selected tab
    tab.setAttribute('aria-selected', 'true');
    tab.setAttribute('tabindex', '0');

    // Show selected panel
    const panel = /** @type {HTMLElement | null} */ (this.querySelector(`#${CSS.escape(panelId)}`));
    if (panel) panel.hidden = false;
  }

  /**
   * Handles IN / CM / INTL toggle for size chart
   * @param {Event} event
   */
  #handleUnitToggle = (event) => {
    const btn = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const unit = btn.dataset.unit;
    const chartContainer = btn.closest('.size-chart');
    if (!chartContainer || !unit) return;

    // Toggle buttons
    chartContainer.querySelectorAll('.size-chart__toggle-btn').forEach((b) => {
      b.setAttribute('aria-pressed', 'false');
    });
    btn.setAttribute('aria-pressed', 'true');

    // Toggle tables
    chartContainer.querySelectorAll('.size-chart__table-wrap').forEach((el) => {
      const table = /** @type {HTMLElement} */ (el);
      table.hidden = table.dataset.unit !== unit;
    });
  };
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
